/**
 * SitePick front — 変更希望フォーム「公開の準備と費用の確認」の表示の切り替え（docs/09 §4c-2）。
 *
 * JS が無くても素の <form> で送れる（全部の質問を表示し、「〜の方のみ」を添える）。ここはその上に:
 *  - `data-sp-show-if`（{key: [値...]}。key ごとに、どれかの値が選ばれていれば表示）に合わせて質問・見出しを出し分ける
 *  - 隠した質問の入力は disabled にする（送らない・必須チェックの対象にしない）。サーバーも同じ条件で捨てる
 *  - 「特になし」（`data-sp-exclusive`）と、ほかの選択肢を同時に選べないようにする
 * 条件の正はサーバーの SetupQuestions::groups()（属性に出したものをそのまま使う）。
 */

/**
 * フォームの今の回答（name="setup[key]" / "setup[key][]"）。
 * @param {HTMLElement} root
 * @returns {Record<string, string[]>}
 */
function readAnswers(root) {
  const out = {};
  root.querySelectorAll("input[name^=\"setup[\"]").forEach(function (input) {
    if (input.disabled) {
      return;
    }
    if ((input.type === "radio" || input.type === "checkbox") && !input.checked) {
      return;
    }
    const m = input.name.match(/^setup\[([a-z_]+)\]/);
    if (!m) {
      return;
    }
    (out[m[1]] = out[m[1]] || []).push(input.value);
  });
  return out;
}

/**
 * @param {Record<string, string[]>} cond
 * @param {Record<string, string[]>} answers
 * @returns {boolean}
 */
function matches(cond, answers) {
  return Object.keys(cond).every(function (key) {
    const picked = answers[key] || [];
    return (cond[key] || []).some(function (v) {
      return picked.indexOf(String(v)) !== -1;
    });
  });
}

/**
 * @param {Element} el
 * @returns {Record<string, string[]> | null}
 */
function conditionOf(el) {
  try {
    const parsed = JSON.parse(el.getAttribute("data-sp-show-if") || "null");
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * 条件に合わせて表示を切り替える。前の質問が隠れると後ろの条件も変わるので、変化が無くなるまで繰り返す。
 * @param {HTMLElement} root
 */
function apply(root) {
  const targets = Array.from(root.querySelectorAll("[data-sp-show-if]"));
  for (let pass = 0; pass < 5; pass += 1) {
    const answers = readAnswers(root);
    let changed = false;
    targets.forEach(function (el) {
      const cond = conditionOf(el);
      const show = cond === null ? true : matches(cond, answers);
      if (el.hidden === !show) {
        return;
      }
      changed = true;
      el.hidden = !show;
    });
    // 隠れている質問（見出しごと隠れている場合を含む）の入力は送らない。
    root.querySelectorAll("input[name^=\"setup[\"], textarea[name^=\"setup[\"]").forEach(function (input) {
      input.disabled = input.closest("[hidden]") !== null;
    });
    if (!changed) {
      break;
    }
  }
}

/**
 * 「特になし」と、ほかの選択肢を同時に選べないようにする。
 * @param {HTMLInputElement} input
 */
function keepExclusive(input) {
  if (input.type !== "checkbox" || !input.checked) {
    return;
  }
  const group = input.closest(".p-onboarding__options");
  if (!group) {
    return;
  }
  group.querySelectorAll("input[type=\"checkbox\"]").forEach(function (other) {
    if (other === input) {
      return;
    }
    const exclusive = input.hasAttribute("data-sp-exclusive");
    if (exclusive || other.hasAttribute("data-sp-exclusive")) {
      other.checked = false;
    }
  });
}

export function initRequirementsSetup() {
  const root = document.querySelector(".js-sp-setup");
  if (!root) {
    return;
  }
  root.classList.add("is-enhanced");
  apply(root);
  root.addEventListener("change", function (event) {
    const target = event.target;
    if (target instanceof HTMLInputElement) {
      keepExclusive(target);
    }
    apply(root);
  });
}
